import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ChatBubbleIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/components/capstone/base';
import { GPX_FEATURES, GPXFeature } from '@rsos/gainsight';
import { useEndConversationMutation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { RootState } from '../../../../app';
import {
  trackEndTF911Click,
  trackEndTF911ModalClick,
  trackEndTF911Response,
} from '../textFrom911Trackings';
import { useEndingMessage } from '../useEndingMessage';
import { EndChatButton, EndChatContainer } from './TF911.styles';
import TF911EndChatModal from './TF911EndChatModal';

interface TF911EndChatProps {
  callerID: string;
  isForOverview: boolean;
  gpxFeature?: GPXFeature;
}

const TF911EndChat = ({
  callerID,
  isForOverview,
  gpxFeature = GPX_FEATURES.TF911,
}: TF911EndChatProps) => {
  const [showEndConversationModal, setShowConversationModal] = useState(false);

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application ?? '',
      )?.name,
  );

  const conversation = useSelectedTextFrom911Conversation(callerID);

  const isEndingConversation = useMemo(
    () => conversation?.loading?.endConversation,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const [endConversation] = useEndConversationMutation();

  const getEndingMessage = useEndingMessage({
    orgName: orgName ?? '',
    callerID,
  });

  const handleEndConversation = useCallback(async () => {
    trackEndTF911ModalClick({
      feature: gpxFeature,
      fromOverviewTab: isForOverview,
      isConfirmed: true,
      conversationId: conversation?.data?.conversation_id,
    });

    const ending = await getEndingMessage();

    endConversation({
      callerID,
      orgName: orgName ?? '',
      endMessage: ending?.body,
      doNotTranslate: ending?.doNotTranslate,
    }).then((response: unknown) => {
      const r = response as { error?: unknown };
      trackEndTF911Response({
        feature: gpxFeature,
        responseSuccess: !r?.error,
        conversationId: conversation?.data?.conversation_id,
      });
      setShowConversationModal(false);
    });
  }, [
    callerID,
    orgName,
    endConversation,
    isForOverview,
    gpxFeature,
    conversation,
    getEndingMessage,
  ]);

  const handleEndChatBtnClick = useCallback(() => {
    if (showEndConversationModal) {
      trackEndTF911ModalClick({
        feature: gpxFeature,
        fromOverviewTab: isForOverview,
        isConfirmed: false,
        conversationId: conversation?.data?.conversation_id,
      });
    } else {
      trackEndTF911Click({
        feature: gpxFeature,
        fromOverviewTab: isForOverview,
        conversationId: conversation?.data?.conversation_id,
      });
    }
    setShowConversationModal(prev => !prev);
  }, [showEndConversationModal, gpxFeature, isForOverview, conversation]);

  return (
    <EndChatContainer>
      <Box
        alignSelf="center"
        borderRadius="0 0 5px 5px"
        horizontal
        width="100%"
      >
        <EndChatButton
          btnType="warning_tertiary"
          disabled={!!(isEndingConversation || isConversationEnded)}
          isLoadingProp={!!isEndingConversation}
          noMargin
          onClick={handleEndChatBtnClick}
          size="med"
          grow
        >
          <ChatBubbleIcon color="lightDanger" />
          End Chat
        </EndChatButton>
      </Box>

      <TF911EndChatModal
        callerID={callerID}
        isOpened={showEndConversationModal}
        isLoading={isEndingConversation}
        onClose={handleEndChatBtnClick}
        endChat={handleEndConversation}
      />
    </EndChatContainer>
  );
};

export default TF911EndChat;
